import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Slider from '@mui/material/Slider'
import classNames from 'classnames'
import { formatAmount } from 'utils/format'
import { AddContractStepProps, AddContractSteps, InstalmentStepError } from '../../types'
import contractStyles from '../../../Contracts.module.scss'
import styles from './InstalmentStep.module.scss'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import { roundToTwoDecimals } from 'utils/number'
import { Card, Form, Heading, InlineMessage, TextField, Toggle } from '@boltenergy-be/design-system'
import parse from 'html-react-parser'
import { BOLT_GO_PRICES } from 'constants/products'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import { useStoreSelector } from 'hooks/store.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { selectCurrentBillingContract } from 'store/contact/slice.ts'
import { EL_PRODUCT_CODES, ENERGY_TYPES, SIMULATION_TYPES, SimulationType } from '@boltenergy-be/shared'

const InstalmentStep = ({ isMove, setNextStep, addContractData }: AddContractStepProps) => {
  // Redux
  const contracts = useStoreSelector(selectCurrentBillingContract)

  // Window size
  const { isTablet } = useWindowSize()

  // Local state
  const [instalmentTotal, setInstalmentTotal] = useState<number>(0)
  const [instalmentElectricity, setInstalmentElectricity] = useState<number>(0)
  const [instalmentGas, setInstalmentGas] = useState<number>(0)
  const [error, setError] = useState<InstalmentStepError>()
  const [simulationType, setSimulationType] = useState<SimulationType>(addContractData.instalmentData.simulationType)

  // i18n
  const { t } = useTranslation(['contracts', 'common'])

  // Constants
  const { needsGas } = addContractData.address
  const minInstalment = 20
  const currentElectricityInstalment = Number(contracts?.serviceContracts?.electricity.detail.instalment) || 20
  const currentGasInstalment = Number(contracts?.serviceContracts?.gas?.detail.instalment) || 0
  const currentInstalment = roundToTwoDecimals(currentElectricityInstalment + currentGasInstalment)
  const maxInstalment = useMemo<number>(() => (currentInstalment ? Math.ceil(currentInstalment / 500) * 500 : 500), [currentInstalment])
  const isBoltGo = addContractData[AddContractSteps.PRODUCT].electricity === EL_PRODUCT_CODES.Go

  /**
   * Function to divide gas and electricity instalment if total instalment changes
   * @param {number} value
   */
  const setInstalments = (value: number) => {
    setInstalmentTotal(value)

    if (needsGas) {
      const elRatio = currentElectricityInstalment === currentInstalment ? 0.5 : currentElectricityInstalment / currentInstalment
      const gasRatio = 1 - elRatio
      setInstalmentElectricity(roundToTwoDecimals(value * elRatio))
      setInstalmentGas(roundToTwoDecimals(value * gasRatio))
    } else {
      setInstalmentElectricity(roundToTwoDecimals(value))
    }
  }

  /**
   * Function that recalculates total instalment after user changed gas or electricity instalment through input
   */
  const recalculateTotalInstalment = () => {
    validate()
    setInstalmentTotal(roundToTwoDecimals(instalmentElectricity + instalmentGas))
  }

  const validate = () => {
    if (simulationType === SIMULATION_TYPES.fixedAmount) {
      if (instalmentTotal < 20) {
        setError({ type: 'total', message: t(($) => $.add.steps.instalment.form.errors.instalmentTotal) })
        return false
      }

      if (needsGas) {
        if (instalmentElectricity === 0) {
          setError({ type: 'electricity', message: t(($) => $.add.steps.instalment.form.errors.instalmentElectricity) })
          return false
        }
        if (instalmentGas === 0) {
          setError({ type: 'gas', message: t(($) => $.add.steps.instalment.form.errors.instalmentGas) })
          return false
        }
      }
    }

    setError(undefined)
    return true
  }

  const onSubmit = () => {
    const valid = validate()

    if (valid) {
      // Clear errors
      setError(undefined)

      // Save data to addContractData state object
      const newInstalmentData = {
        simulationType,
        instalments: ([SIMULATION_TYPES.fixedAmount, SIMULATION_TYPES.boltGo] as SimulationType[]).includes(simulationType)
          ? {
              electricity: needsGas ? instalmentElectricity : instalmentTotal,
              ...(needsGas && { gas: instalmentGas })
            }
          : {
              electricity: 0
            }
      }

      setNextStep(newInstalmentData, AddContractSteps.METER_DETAILS)
    }
  }

  /**
   * Set default instalment values
   */
  useEffect(() => {
    if (isBoltGo) {
      if (
        ([SIMULATION_TYPES.eav, SIMULATION_TYPES.fixedAmount] as SimulationType[]).includes(addContractData.instalmentData.simulationType)
      )
        setSimulationType(SIMULATION_TYPES.boltGo)
      if (needsGas) {
        setInstalmentElectricity(BOLT_GO_PRICES[ENERGY_TYPES.electricity])
        setInstalmentGas(BOLT_GO_PRICES[ENERGY_TYPES.gas])
        setInstalmentTotal(BOLT_GO_PRICES[ENERGY_TYPES.electricity] + BOLT_GO_PRICES[ENERGY_TYPES.gas])
      } else {
        setInstalmentTotal(BOLT_GO_PRICES[ENERGY_TYPES.electricity])
      }
    } else if (addContractData.instalmentData.instalments.electricity !== 0) {
      if (simulationType === SIMULATION_TYPES.boltGo) setSimulationType(SIMULATION_TYPES.eav)
      const { instalmentData } = addContractData

      if (simulationType === SIMULATION_TYPES.fixedAmount) {
        if (typeof instalmentData.instalments.gas !== 'undefined' && needsGas) {
          setInstalmentElectricity(instalmentData.instalments.electricity)
          setInstalmentGas(instalmentData.instalments.gas)
          setInstalmentTotal(instalmentData.instalments.electricity + instalmentData.instalments.gas)
        } else {
          setInstalmentTotal(instalmentData.instalments.electricity)
        }
      }
    } else {
      // Set default instalment to instalment of current address
      if (needsGas) {
        const hasNoGas = roundToTwoDecimals(currentElectricityInstalment) === currentInstalment && !currentGasInstalment
        setInstalmentElectricity(roundToTwoDecimals(hasNoGas ? currentElectricityInstalment / 2 : currentElectricityInstalment))
        setInstalmentGas(roundToTwoDecimals(hasNoGas ? currentElectricityInstalment / 2 : currentGasInstalment))
        setInstalmentTotal(currentInstalment)
      } else {
        setInstalmentTotal(currentInstalment)
      }
    }
  }, [isBoltGo, currentInstalment, needsGas])

  return (
    <section className={classNames(contractStyles['form-card'], styles['instalment-step'])}>
      <Heading as="h1" variant="h4">
        {t(($) => (isBoltGo ? $.add.steps.instalment.boltGo.title : $.add.steps.instalment.title))}
      </Heading>

      <div className={contractStyles['form-content']}>
        {!isBoltGo && (
          <Toggle
            isFullwidth
            active={simulationType}
            onClick={(value) => setSimulationType(value)}
            options={[
              { value: SIMULATION_TYPES.eav, label: t(($) => $.add.steps.instalment.form.EAV.toggleLabel) },
              { value: SIMULATION_TYPES.fixedAmount, label: t(($) => $.add.steps.instalment.form.fixedAmount.toggleLabel) }
            ]}
          />
        )}
        {simulationType === SIMULATION_TYPES.eav ? (
          <p className={styles.description}>{t(($) => $.add.steps.instalment.form.EAV.description)}</p>
        ) : isBoltGo ? (
          <div className={styles['description-container']}>{parse(t(($) => $.add.steps.instalment.boltGo.description))}</div>
        ) : (
          <>
            <div className={styles.description}>
              {t(($) => $.add.steps.instalment.form.fixedAmount.description[isMove ? 'move' : 'add'])}
            </div>
            <div className={styles['slider-container']}>
              <Slider
                className={styles['instalment-slider']}
                min={minInstalment}
                max={maxInstalment}
                step={5}
                onChange={(_, value) => setInstalments(Number(value))}
                value={instalmentTotal}
              />
              <div className={classNames(styles['mark-label'], styles['mark-label-min'])}>
                <data value={formatAmount(minInstalment)} className={styles.positioner}>
                  € {formatAmount(minInstalment)}
                </data>
              </div>
              <div className={classNames(styles['mark-label'], styles['mark-label-max'])}>
                <data value={formatAmount(maxInstalment)} className={styles.positioner}>
                  € {formatAmount(maxInstalment)}
                </data>
              </div>
            </div>
          </>
        )}

        {simulationType !== SIMULATION_TYPES.eav && (
          <Card color={isTablet ? 'white' : 'neutral'} className={styles['input-card']}>
            <Form>
              {needsGas && (
                <>
                  <TextField
                    grid="full"
                    label={t(($) => $.add.steps.instalment.form.stepSummary.fixedAmount.labels.instalmentElectricity)}
                    prefix={{ as: '€' }}
                    type="number"
                    step="1"
                    name="electricityInstalment"
                    value={instalmentElectricity}
                    onChange={(e) => setInstalmentElectricity(Number(e.target.value))}
                    error={error?.type === 'electricity'}
                    onBlur={recalculateTotalInstalment}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        recalculateTotalInstalment()
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    disabled={isBoltGo}
                  />

                  <TextField
                    grid="full"
                    label={t(($) => $.add.steps.instalment.form.stepSummary.fixedAmount.labels.instalmentGas)}
                    prefix={{ as: '€' }}
                    type="number"
                    step="1"
                    name="gasInstalment"
                    value={instalmentGas}
                    onChange={(e) => setInstalmentGas(Number(e.target.value))}
                    error={error?.type === 'gas'}
                    onBlur={recalculateTotalInstalment}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        recalculateTotalInstalment()
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    disabled={isBoltGo}
                  />
                </>
              )}

              <TextField
                grid="full"
                label={
                  needsGas
                    ? t(($) =>
                        isBoltGo
                          ? $.add.steps.instalment.boltGo.instalmentTotal
                          : $.add.steps.instalment.form.stepSummary.fixedAmount.labels.instalmentTotal
                      )
                    : t(($) =>
                        isBoltGo
                          ? $.add.steps.instalment.boltGo.instalmentTotalOnlyElec
                          : $.add.steps.instalment.form.stepSummary.fixedAmount.labels.instalmentTotalOnlyElec
                      )
                }
                prefix={{ as: '€' }}
                type="number"
                step="1"
                name="totalInstalment"
                value={instalmentTotal}
                min={minInstalment}
                onChange={(e) => setInstalments(Number(e.target.value))}
                error={error?.type === 'total'}
                onBlur={recalculateTotalInstalment}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={isBoltGo}
              />
            </Form>

            {simulationType === SIMULATION_TYPES.fixedAmount && error && <InlineMessage type="negative">{error.message}</InlineMessage>}
          </Card>
        )}
      </div>

      <FormButtons currentStep={AddContractSteps.INSTALMENT_DATA} trackingId={ContractFlowTrackingTypes.ADD} onSubmit={onSubmit} />
    </section>
  )
}

export default InstalmentStep
